import { v7 } from 'uuid';

import { logger } from '@/shared/libs/logger';

export interface QueuedEvent {
  id: string;
  type: 'track' | 'page' | 'identify' | 'setAnonymousId';
  eventName?: string;
  payload?: Record<string, any>;
  userId?: string;
  anonymousId?: string;
  timestamp: number;
}

export interface StorageAdapter {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

export interface EventQueueConfig {
  storageKey: string;
  maxQueueSize: number;
  persistTimeMs: number;
  storage: StorageAdapter;
}

export function generateEventId(): string {
  return `tg-analytics-event-${v7()}`;
}

export function filterExpiredEvents(
  events: QueuedEvent[],
  maxAgeMs: number,
  currentTime: number = Date.now(),
): QueuedEvent[] {
  return events.filter((event) => currentTime - event.timestamp < maxAgeMs);
}

export function addEventToQueue(
  queue: QueuedEvent[],
  event: Omit<QueuedEvent, 'id' | 'timestamp'>,
  maxSize: number,
): QueuedEvent[] {
  const queuedEvent: QueuedEvent = {
    ...event,
    id: generateEventId(),
    timestamp: Date.now(),
  };

  // Keep queue size manageable by removing oldest events
  const newQueue = queue.length >= maxSize ? queue.slice(-(maxSize - 1)) : queue;

  return [...newQueue, queuedEvent];
}

// Storage operations with error handling
export class EventQueueStorage {
  constructor(private readonly config: EventQueueConfig) {}

  getQueue(): QueuedEvent[] {
    try {
      const stored = this.config.storage.getItem(this.config.storageKey);
      if (!stored) {
        return [];
      }

      const parsed = JSON.parse(stored);
      return filterExpiredEvents(parsed, this.config.persistTimeMs);
    } catch (error) {
      logger.warn('Failed to retrieve analytics queue from storage', undefined, error);
      return [];
    }
  }

  saveQueue(queue: QueuedEvent[]): void {
    try {
      this.config.storage.setItem(this.config.storageKey, JSON.stringify(queue));
    } catch (error) {
      logger.warn('Failed to save analytics queue to storage', undefined, error);
    }
  }

  clearQueue(): void {
    try {
      this.config.storage.removeItem(this.config.storageKey);
    } catch (error) {
      logger.warn('Failed to clear analytics queue from storage', undefined, error);
    }
  }
}

export class EventQueueManager {
  private queue: QueuedEvent[] = [];
  private readonly storage: EventQueueStorage;

  constructor(
    private config: EventQueueConfig,
    initialQueue?: QueuedEvent[],
  ) {
    this.storage = new EventQueueStorage(config);
    this.queue = initialQueue || this.storage.getQueue();
  }

  addEvent(event: Omit<QueuedEvent, 'id' | 'timestamp'>): void {
    this.queue = addEventToQueue(this.queue, event, this.config.maxQueueSize);
    this.storage.saveQueue(this.queue);
  }

  getEvents(): QueuedEvent[] {
    return [...this.queue];
  }

  clearQueue(): void {
    this.queue = [];
    this.storage.clearQueue();
  }

  getEventCount(): number {
    return this.queue.length;
  }

  removeEvents(eventIds: string[]): void {
    this.queue = this.queue.filter((event) => !eventIds.includes(event.id));
    this.storage.saveQueue(this.queue);
  }
}
